import { useState, useMemo } from 'react';
import { STATUS_MAP, LICENSE_OPTIONS } from '../employeeConstants';
import { getStatus, formatLicenses, getReplacementName } from '../employeeUtils';

const NO_LICENSE_ID = 'no_license';

/**
 * Hook to manage employee filtering and sorting.
 */
export function useEmployeeFilters(employees, categories, absences, view) {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    
    // FILTERS
    const [filterStatus, setFilterStatus] = useState(['present', 'vacation', 'sick', 'appointment', 'other']);
    const [filterCategory, setFilterCategory] = useState([]); // Will be filled via useEffect
    const [filterWorker, setFilterWorker] = useState([]);     // Will be filled via useEffect
    const [filterLicense, setFilterLicense] = useState([...LICENSE_OPTIONS, 'no_license']);
    const [filtersRowVisible, setFiltersRowVisible] = useState(false);

    // Initialize category and worker filters when data arrives
    useMemo(() => {
        if (categories.length > 0 && filterCategory.length === 0) {
            setFilterCategory(categories.map(c => String(c.id)).concat(['null']));
        }
    }, [categories]);

    useMemo(() => {
        if (employees.length > 0 && filterWorker.length === 0) {
            setFilterWorker(employees.map(e => String(e.id)));
        }
    }, [employees]);

    const processedEmployees = useMemo(() => {
        let items = [...employees];

        // 1. Filter by Search Term
        if (searchTerm) {
            const low = searchTerm.toLowerCase();
            items = items.filter(e => 
                (e.name || '').toLowerCase().includes(low) ||
                (e.category_name || '').toLowerCase().includes(low) ||
                (e.email || '').toLowerCase().includes(low)
            );
        }

        // 2. Filter by Status (Only in Calendar/Absence View)
        const allStatusKeys = Object.keys(STATUS_MAP);
        if (view === 'calendar') {
            if (filterStatus.length === 0) {
                items = [];
            } else if (filterStatus.length < allStatusKeys.length) {
                const selectedSet = new Set(filterStatus);
                items = items.filter(e => selectedSet.has(getStatus(e.id, absences).type));
            }
        }

        // 3. Filter by Role
        if (categories.length > 0) {
            if (filterCategory.length === 0) {
                items = [];
            } else if (filterCategory.length < categories.length) {
                const selectedSet = new Set(filterCategory.map(id => String(id)));
                items = items.filter(e => {
                    const cid = e.employee_category_id ? String(e.employee_category_id) : 'null';
                    return selectedSet.has(cid);
                });
            }
        }

        // 4. Filter by Worker Name
        if (employees.length > 0) {
            if (filterWorker.length === 0) {
                items = [];
            } else if (filterWorker.length < employees.length) {
                const selectedSet = new Set(filterWorker.map(id => String(id)));
                items = items.filter(e => selectedSet.has(String(e.id)));
            }
        }

        // 5. Filter by License
        const allLicenseOptionsCount = LICENSE_OPTIONS.length + 1;
        if (view !== 'calendar' && filterLicense.length < allLicenseOptionsCount) {
            if (filterLicense.length === 0) {
                items = [];
            } else {
                const selectedSet = new Set(filterLicense);
                items = items.filter(e => {
                    const cats = (e.license_categories || []).map(l => String(l).toUpperCase());
                    const classUpper = (e.license_class || '').toUpperCase();
                    const hasNoLicense = cats.length === 0 && !classUpper;
                    if (hasNoLicense) return selectedSet.has(NO_LICENSE_ID);
                    return selectedSet.has(classUpper) || cats.some(c => selectedSet.has(c));
                });
            }
        }

        // Sort
        items.sort((a, b) => {
            let aVal, bVal;
            
            switch(sortConfig.key) {
                case 'role': aVal = a.category_name || ''; bVal = b.category_name || ''; break;
                case 'status': aVal = getStatus(a.id, absences).label; bVal = getStatus(b.id, absences).label; break;
                case 'license': aVal = formatLicenses(a); bVal = formatLicenses(b); break;
                case 'replacement': aVal = getReplacementName(a.vacation_replacement_id, employees); bVal = getReplacementName(b.vacation_replacement_id, employees); break;
                case 'contact': aVal = (a.email || a.phone || ''); bVal = (b.email || b.phone || ''); break;
                default: aVal = a[sortConfig.key] || ''; bVal = b[sortConfig.key] || '';
            }

            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();
            
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return items;
    }, [employees, searchTerm, sortConfig, filterStatus, filterCategory, filterWorker, filterLicense, absences, view, categories.length]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const isFilterActive = 
        (view === 'calendar' && filterStatus.length < Object.keys(STATUS_MAP).length) ||
        (categories.length > 0 && filterCategory.length < categories.length) ||
        (employees.length > 0 && filterWorker.length < employees.length) ||
        (view !== 'calendar' && filterLicense.length < (LICENSE_OPTIONS.length + 1));

    return {
        searchTerm, setSearchTerm,
        sortConfig, handleSort,
        filterStatus, setFilterStatus,
        filterCategory, setFilterCategory,
        filterWorker, setFilterWorker,
        filterLicense, setFilterLicense,
        filtersRowVisible, setFiltersRowVisible,
        processedEmployees,
        isFilterActive
    };
}
